import { useCallback, useEffect, useRef, useState } from "react";
import { ESupportLanguageOfSupport } from "@devowl-wp/api-real-commerce";
import { locationRestQueryGet } from "@devowl-wp/search-engine-post-type";
import { request } from "../utils/request.js";
function mapSupportLanguageToKnowledgeBaseDataLocale(language) {
    switch(language){
        case ESupportLanguageOfSupport.German:
            return "de_DE";
        default:
            return "en_US";
    }
}
/**
 * **Note**: The knowledge base articles are currently fetched through the `devowl-site` plugin due to backwards-
 * compatibility!
 */ function useKnowledgeBaseArticles(debounce, initialDataLocale) {
    if (debounce === void 0) debounce = 0;
    const [knowledgeBaseHits, setKnowledgeBaseHits] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [dataLocale, setDataLocale] = useState(initialDataLocale);
    const { current } = useRef({
        lastDataLocale: initialDataLocale,
        lastDebounce: undefined,
        lastTerm: "",
        lastSlugs: ""
    });
    const [debounceTimeout, setDebounceTimeout] = useState();
    const handleSearch = useCallback(async (term, slugs)=>{
        if (term) {
            try {
                const { hits } = await request({
                    location: locationRestQueryGet,
                    params: {
                        name: "knowledge-base-articles",
                        term,
                        taxonomy: "topic",
                        slugs,
                        hitsPerPage: 3,
                        ...dataLocale ? {
                            _dataLocale: dataLocale
                        } : {}
                    },
                    sendRestNonce: false
                });
                setKnowledgeBaseHits(hits);
                return hits;
            } catch (e) {
                setKnowledgeBaseHits([]);
                return [];
            } finally{
                setLoading(false);
            }
        } else {
            setLoading(false);
            setKnowledgeBaseHits(undefined);
            return [];
        }
    }, [
        setKnowledgeBaseHits,
        dataLocale
    ]);
    // Debounce the search
    const handleSearchKnowledgeBaseHits = useCallback((term, slugs, overrideDebounce)=>new Promise((resolve)=>{
            // Avoid duplicate terms
            const useDebounce = typeof overrideDebounce === "number" ? overrideDebounce : debounce;
            const { lastTerm, lastSlugs, lastDebounce, lastDataLocale } = current;
            if (lastTerm === term && lastSlugs === slugs && lastDebounce === useDebounce && lastDataLocale === dataLocale) {
                return;
            }
            current.lastTerm = term;
            current.lastSlugs = slugs;
            current.lastDebounce = useDebounce;
            current.lastDataLocale = dataLocale;
            setLoading(true);
            clearTimeout(debounceTimeout);
            setDebounceTimeout(setTimeout(()=>{
                handleSearch(term, slugs).then(resolve);
            }, useDebounce));
        }), [
        handleSearch,
        debounceTimeout,
        debounce,
        current
    ]);
    useEffect(()=>{
        handleSearchKnowledgeBaseHits(current.lastTerm, current.lastSlugs, 0);
    }, [
        dataLocale
    ]);
    return {
        loading,
        dataLocale,
        setDataLocale,
        knowledgeBaseHits,
        handleSearchKnowledgeBaseHits
    };
}
export { mapSupportLanguageToKnowledgeBaseDataLocale, useKnowledgeBaseArticles };
